// Additional Service Categories Data for CareerBuddyPro

export const contentCreationData = {
  title: "Content Creation & Copywriting",
  subtitle: "Engaging Content That Converts",
  heroDescription: "Create compelling content with professional writers and creators who understand your audience. From blog posts to social media content, video scripts to technical documentation, find creators who bring your brand voice to life.",
  heroStats: [
    { value: "35K+", label: "Content Creators" },
    { value: "1M+", label: "Pieces Created" },
    { value: "85%", label: "Engagement Increase" },
    { value: "48h", label: "Average Turnaround" }
  ],
  whyCareerBuddyPro: {
    description: "CareerBuddyPro's content creators have produced award-winning campaigns for Fortune 500 brands and viral content for growing startups. Find creators who understand your industry and audience.",
    benefits: [
      {
        icon: "✍️",
        title: "Expert Writers",
        description: "Specialized creators in every niche - from technical writing to creative storytelling and conversion copywriting."
      },
      {
        icon: "🎨",
        title: "Multi-Format Content",
        description: "Blog posts, social media, video scripts, email campaigns, whitepapers, and more - all from vetted creators."
      },
      {
        icon: "📊",
        title: "Performance-Driven",
        description: "Creators who understand SEO, conversion optimization, and how to create content that drives business results."
      }
    ]
  },
  serviceDetails: {
    title: "Complete Content Creation Services",
    description: "Our creators handle every type of content your business needs, from strategy to execution across all channels and formats.",
    categories: [
      {
        title: "Written Content",
        services: [
          { name: "Blog Writing", description: "SEO-optimized articles that drive traffic and establish thought leadership" },
          { name: "Copywriting", description: "High-converting sales copy, landing pages, and marketing materials" },
          { name: "Technical Writing", description: "Documentation, guides, and complex technical content made simple" },
          { name: "Social Media Copy", description: "Engaging posts, captions, and campaigns for all major platforms" },
          { name: "Email Marketing", description: "Newsletter content and automated email sequences that convert" }
        ]
      },
      {
        title: "Visual & Video Content",
        services: [
          { name: "Video Scripts", description: "Compelling scripts for explainer videos, ads, and social media content" },
          { name: "Podcast Content", description: "Show notes, episode scripts, and podcast marketing content" },
          { name: "Content Strategy", description: "Comprehensive content calendars and strategic planning" },
          { name: "Case Studies", description: "Detailed success stories that showcase your impact and results" },
          { name: "Whitepapers & eBooks", description: "In-depth content that positions you as an industry authority" }
        ]
      }
    ]
  },
  testimonials: [
    {
      quote: "The content creator we hired through CareerBuddyPro increased our blog traffic by 400% in just 6 months. The quality and SEO optimization were outstanding.",
      name: "Sarah Mitchell",
      title: "Marketing Manager",
      company: "GrowthHub"
    },
    {
      quote: "Our conversion rates improved dramatically after working with a copywriter from CareerBuddyPro. They understood our audience perfectly and delivered results.",
      name: "Tom Bradley",
      title: "Founder",
      company: "ConvertPro"
    },
    {
      quote: "The technical writer we found made our complex product documentation clear and user-friendly. Customer support tickets dropped by 30%.",
      name: "Lisa Wang",
      title: "Product Manager",
      company: "TechFlow"
    }
  ],
  faqSection: [
    {
      question: "How do I brief a content creator effectively?",
      answer: "Provide clear goals, target audience details, brand voice guidelines, key messages, and examples of content you like. Our creators will ask clarifying questions to ensure alignment."
    },
    {
      question: "What's the typical turnaround time for content?",
      answer: "Blog posts typically take 3-5 days, while complex content like whitepapers may take 1-2 weeks. Rush delivery is available for urgent projects."
    },
    {
      question: "Do content creators handle SEO optimization?",
      answer: "Yes, many creators specialize in SEO-optimized content including keyword research, on-page optimization, and content structure for better search rankings."
    },
    {
      question: "Can creators match my brand voice?",
      answer: "Absolutely. Professional creators study your existing content, brand guidelines, and tone of voice to ensure consistency across all content they produce."
    }
  ],
  ctaSection: {
    title: "Create Content That Drives Results",
    description: "Stop struggling with content creation. Find expert creators who understand your audience and deliver content that converts.",
    primaryButton: "Find Content Creators",
    secondaryButton: "Post Content Project"
  }
};

export const designServicesData = {
  title: "Design Services",
  subtitle: "Beautiful, User-Centered Design Solutions",
  heroDescription: "Create stunning visual experiences with top designers who combine creativity with strategic thinking. From UI/UX design to brand identity, logos to complete design systems, find designers who make your vision reality.",
  heroStats: [
    { value: "40K+", label: "Designers" },
    { value: "95%", label: "Client Satisfaction" },
    { value: "72h", label: "First Concept Delivery" },
    { value: "3.2x", label: "Conversion Improvement" }
  ],
  whyCareerBuddyPro: {
    description: "CareerBuddyPro's designers have created award-winning designs for global brands and innovative startups. Access talent who understand both aesthetics and user psychology.",
    benefits: [
      {
        icon: "🎨",
        title: "Creative Excellence",
        description: "Award-winning designers with portfolios showcasing work for leading brands and successful products."
      },
      {
        icon: "👥",
        title: "User-Centered Design",
        description: "Designers who combine beautiful aesthetics with user research and data-driven design decisions."
      },
      {
        icon: "⚡",
        title: "Fast Iteration",
        description: "Agile design process with rapid prototyping and iteration based on feedback and testing."
      }
    ]
  },
  serviceDetails: {
    title: "Comprehensive Design Solutions",
    description: "Our designers create cohesive visual experiences across all touchpoints, from digital products to brand identity and marketing materials.",
    categories: [
      {
        title: "Digital Product Design",
        services: [
          { name: "UI/UX Design", description: "User interfaces that are beautiful, intuitive, and conversion-optimized" },
          { name: "Mobile App Design", description: "Native iOS and Android designs following platform guidelines" },
          { name: "Web Design", description: "Responsive website designs that engage users and drive conversions" },
          { name: "Design Systems", description: "Scalable design systems with components, patterns, and guidelines" },
          { name: "Prototyping", description: "Interactive prototypes for testing and stakeholder presentations" }
        ]
      },
      {
        title: "Brand & Marketing Design",
        services: [
          { name: "Brand Identity", description: "Logos, color palettes, typography, and complete brand guidelines" },
          { name: "Marketing Materials", description: "Brochures, presentations, social media graphics, and advertising" },
          { name: "Packaging Design", description: "Product packaging that stands out and communicates value" },
          { name: "Print Design", description: "Business cards, letterheads, posters, and other print materials" },
          { name: "Illustration", description: "Custom illustrations for websites, apps, and marketing campaigns" }
        ]
      }
    ]
  },
  testimonials: [
    {
      quote: "The UI/UX designer we hired through CareerBuddyPro completely transformed our app. User engagement increased by 60% and our App Store rating improved to 4.8 stars.",
      name: "Kevin Chen",
      title: "Product Director",
      company: "AppSuccess"
    },
    {
      quote: "Our brand identity redesign was phenomenal. The designer captured our vision perfectly and created a cohesive system that works across all channels.",
      name: "Amanda Foster",
      title: "Brand Manager",
      company: "BrandCorp"
    },
    {
      quote: "The website redesign increased our conversion rate by 150%. The designer understood our users and created an experience that drives results.",
      name: "Ryan Johnson",
      title: "Marketing Director",
      company: "ConvertPlus"
    }
  ],
  faqSection: [
    {
      question: "How do I choose between different design specializations?",
      answer: "Consider your primary needs: UI/UX designers for digital products, brand designers for identity work, or generalist designers for comprehensive projects. Review portfolios that match your project type."
    },
    {
      question: "What's included in a typical design project?",
      answer: "Projects typically include research, concepts, revisions, final files, and guidelines. Specific deliverables vary by project type and are defined upfront with your designer."
    },
    {
      question: "How many concepts will I receive?",
      answer: "Most designers provide 2-3 initial concepts for feedback, then iterate on the chosen direction. The exact process is agreed upon before starting the project."
    },
    {
      question: "Do designers provide files in different formats?",
      answer: "Yes, professional designers provide source files (Sketch, Figma, Adobe) and export files in various formats (PNG, JPG, SVG, PDF) as needed for implementation."
    }
  ],
  ctaSection: {
    title: "Design Experiences That Convert",
    description: "Great design drives business results. Find designers who combine creativity with strategy to achieve your goals.",
    primaryButton: "Find Designers",
    secondaryButton: "Post Design Project"
  }
};

export const digitalMarketingData = {
  title: "Digital Marketing",
  subtitle: "Grow Your Business with Expert Marketing",
  heroDescription: "Scale your business with marketing experts who drive measurable results. From PPC campaigns to social media marketing, SEO to conversion optimization, find specialists who understand your industry and audience.",
  heroStats: [
    { value: "30K+", label: "Marketing Experts" },
    { value: "250%", label: "Average ROI Increase" },
    { value: "90%", label: "Campaign Success Rate" },
    { value: "5M+", label: "Leads Generated" }
  ],
  whyCareerBuddyPro: {
    description: "CareerBuddyPro's marketing experts have grown businesses from startups to Fortune 500 companies. Access specialists with proven track records and cutting-edge strategies.",
    benefits: [
      {
        icon: "📊",
        title: "Data-Driven Results",
        description: "Marketers who use analytics and testing to optimize campaigns for maximum ROI and performance."
      },
      {
        icon: "🎯",
        title: "Specialized Expertise",
        description: "Certified specialists in Google Ads, Facebook Ads, SEO, content marketing, and all major platforms."
      },
      {
        icon: "🚀",
        title: "Growth-Focused",
        description: "Proven strategies for scaling businesses, from lead generation to customer retention and lifetime value."
      }
    ]
  },
  serviceDetails: {
    title: "Complete Digital Marketing Solutions",
    description: "Our marketing experts handle every aspect of digital marketing, from strategy to execution and optimization across all channels.",
    categories: [
      {
        title: "Paid Advertising",
        services: [
          { name: "Google Ads Management", description: "Search, display, and shopping campaigns optimized for conversions" },
          { name: "Facebook & Instagram Ads", description: "Social media advertising with advanced targeting and creative testing" },
          { name: "LinkedIn Advertising", description: "B2B focused campaigns for lead generation and brand awareness" },
          { name: "YouTube Marketing", description: "Video advertising and channel optimization for engagement and growth" },
          { name: "PPC Strategy & Optimization", description: "Campaign strategy, keyword research, and continuous optimization" }
        ]
      },
      {
        title: "Organic Marketing",
        services: [
          { name: "Search Engine Optimization", description: "Technical and content SEO for better rankings and organic traffic" },
          { name: "Social Media Marketing", description: "Organic social strategies, content creation, and community management" },
          { name: "Email Marketing", description: "Newsletter campaigns, automation, and customer retention strategies" },
          { name: "Content Marketing", description: "Strategic content creation and distribution for brand awareness" },
          { name: "Conversion Rate Optimization", description: "Website and landing page optimization for better conversion rates" }
        ]
      }
    ]
  },
  testimonials: [
    {
      quote: "The Google Ads specialist we hired through CareerBuddyPro reduced our cost per acquisition by 45% while tripling our lead volume. Incredible results!",
      name: "Michelle Roberts",
      title: "VP of Marketing",
      company: "LeadGen Pro"
    },
    {
      quote: "Our SEO rankings improved dramatically. We're now on the first page for all our target keywords and organic traffic increased by 300%.",
      name: "David Lee",
      title: "Digital Marketing Manager",
      company: "SearchSuccess"
    },
    {
      quote: "The social media marketing expert created a strategy that increased our follower engagement by 400% and drove significant sales growth.",
      name: "Jessica Taylor",
      title: "Brand Manager",
      company: "SocialGrowth"
    }
  ],
  faqSection: [
    {
      question: "How do I know which marketing channels to focus on?",
      answer: "Marketing experts analyze your business, target audience, and goals to recommend the most effective channels. They often suggest a multi-channel approach for best results."
    },
    {
      question: "What's a realistic timeline for seeing marketing results?",
      answer: "Paid advertising can show results within days, while SEO and content marketing typically take 3-6 months. Your specialist will set realistic expectations based on your strategy."
    },
    {
      question: "Do marketers provide regular reporting?",
      answer: "Yes, professional marketers provide regular reports with key metrics, insights, and recommendations. Reporting frequency is typically weekly or monthly depending on campaign type."
    },
    {
      question: "Can marketing experts work with my existing tools?",
      answer: "Most experts are familiar with major marketing tools (Google Analytics, HubSpot, Salesforce, etc.) and can work with your existing tech stack or recommend improvements."
    }
  ],
  ctaSection: {
    title: "Scale Your Business with Expert Marketing",
    description: "Stop guessing about marketing. Find experts who deliver measurable results and help you achieve your growth goals.",
    primaryButton: "Find Marketing Experts",
    secondaryButton: "Get Marketing Quote"
  }
};

export const businessConsultingData = {
  title: "Business Consulting & Strategy",
  subtitle: "Strategic Guidance for Business Growth",
  heroDescription: "Transform your business with expert consultants who provide strategic insights, operational improvements, and growth strategies. From business planning to digital transformation, find consultants who drive measurable results.",
  heroStats: [
    { value: "18K+", label: "Business Consultants" },
    { value: "89%", label: "Goal Achievement Rate" },
    { value: "180%", label: "Average Growth Increase" },
    { value: "500+", label: "Industries Served" }
  ],
  whyCareerBuddyPro: {
    description: "CareerBuddyPro's business consultants are former executives, MBAs, and strategic advisors from leading consulting firms and Fortune 500 companies. Get access to senior-level expertise.",
    benefits: [
      {
        icon: "🎯",
        title: "Strategic Focus",
        description: "Consultants who align strategic initiatives with business goals and provide actionable roadmaps for growth."
      },
      {
        icon: "📈",
        title: "Proven Methodologies",
        description: "Experienced consultants using frameworks from McKinsey, BCG, and other top firms to solve complex business challenges."
      },
      {
        icon: "⚡",
        title: "Rapid Impact",
        description: "Immediate insights and quick wins while building long-term strategic capabilities for sustained growth."
      }
    ]
  },
  serviceDetails: {
    title: "Comprehensive Business Consulting Services",
    description: "Our consultants provide strategic guidance across all business functions, from high-level strategy to operational excellence and implementation.",
    categories: [
      {
        title: "Strategic Consulting",
        services: [
          { name: "Business Strategy", description: "Strategic planning, market analysis, and competitive positioning" },
          { name: "Growth Strategy", description: "Market expansion, product development, and scaling strategies" },
          { name: "Digital Transformation", description: "Technology adoption and process digitization strategies" },
          { name: "Market Entry", description: "New market analysis, entry strategies, and competitive intelligence" },
          { name: "Business Model Innovation", description: "Revenue model optimization and business model transformation" }
        ]
      },
      {
        title: "Operational Excellence",
        services: [
          { name: "Process Optimization", description: "Workflow improvement, efficiency gains, and cost reduction strategies" },
          { name: "Change Management", description: "Organizational transformation and change implementation strategies" },
          { name: "Performance Management", description: "KPI frameworks, performance measurement, and improvement systems" },
          { name: "Financial Planning", description: "Financial modeling, budgeting, and investment planning" },
          { name: "Risk Management", description: "Risk assessment, mitigation strategies, and compliance frameworks" }
        ]
      }
    ]
  },
  testimonials: [
    {
      quote: "The business consultant we hired through CareerBuddyPro developed a growth strategy that doubled our revenue in 18 months. Their insights were transformational.",
      name: "Caroline White",
      title: "CEO",
      company: "GrowthCorp"
    },
    {
      quote: "Our digital transformation project was expertly guided from strategy to implementation. We achieved 40% efficiency gains across all operations.",
      name: "Marcus Johnson",
      title: "COO",
      company: "TransformTech"
    },
    {
      quote: "The market entry strategy they developed helped us successfully launch in 3 new countries. Their research and planning were exceptional.",
      name: "Elena Rodriguez",
      title: "VP of Strategy",
      company: "GlobalExpansion"
    }
  ],
  faqSection: [
    {
      question: "When should I hire a business consultant?",
      answer: "Consider consulting when facing strategic decisions, planning growth initiatives, entering new markets, improving operations, or navigating major business challenges."
    },
    {
      question: "What's the typical duration of consulting engagements?",
      answer: "Engagements range from 2-week strategic assessments to 12-month transformation projects. Most strategic projects last 3-6 months with defined milestones and deliverables."
    },
    {
      question: "How do consultants work with my existing team?",
      answer: "Consultants typically work closely with your leadership team, providing guidance, training, and knowledge transfer to ensure sustainable implementation of recommendations."
    },
    {
      question: "What deliverables can I expect from consulting projects?",
      answer: "Typical deliverables include strategic assessments, roadmaps, implementation plans, financial models, process documentation, and training materials."
    }
  ],
  ctaSection: {
    title: "Accelerate Your Business Growth",
    description: "Get the strategic guidance you need to overcome challenges and achieve your business goals. Connect with expert consultants today.",
    primaryButton: "Find Business Consultants",
    secondaryButton: "Schedule Strategy Call"
  }
};

export const dataAnalyticsData = {
  title: "Data Analytics & Business Intelligence",
  subtitle: "Turn Data Into Actionable Business Insights",
  heroDescription: "Make data-driven decisions with analytics experts who transform raw data into strategic insights. From dashboard creation to predictive analytics, find specialists who help you understand your business and customers better.",
  heroStats: [
    { value: "22K+", label: "Data Analysts" },
    { value: "75%", label: "Decision Accuracy Improvement" },
    { value: "40%", label: "Average Cost Reduction" },
    { value: "99.9%", label: "Data Accuracy Rate" }
  ],
  whyCareerBuddyPro: {
    description: "CareerBuddyPro's data analysts and scientists have helped businesses unlock millions in value through better data insights. Access experts with experience across industries and analytics tools.",
    benefits: [
      {
        icon: "📊",
        title: "Advanced Analytics",
        description: "Experts in statistical analysis, predictive modeling, and machine learning for sophisticated insights."
      },
      {
        icon: "🔍",
        title: "Business Focus",
        description: "Analysts who understand business context and translate technical insights into actionable recommendations."
      },
      {
        icon: "⚡",
        title: "Tool Expertise",
        description: "Certified specialists in Tableau, Power BI, Python, R, SQL, and all major analytics platforms."
      }
    ]
  },
  serviceDetails: {
    title: "Complete Data Analytics Solutions",
    description: "Our analytics experts handle everything from data collection and cleaning to advanced analysis and visualization, delivering insights that drive business value.",
    categories: [
      {
        title: "Business Intelligence",
        services: [
          { name: "Dashboard Creation", description: "Interactive dashboards and reports for real-time business monitoring" },
          { name: "Data Visualization", description: "Clear, compelling charts and graphs that communicate insights effectively" },
          { name: "KPI Tracking", description: "Performance measurement systems and automated reporting" },
          { name: "Data Warehousing", description: "Data integration, storage, and management solutions" },
          { name: "Self-Service Analytics", description: "User-friendly tools for non-technical team members to explore data" }
        ]
      },
      {
        title: "Advanced Analytics",
        services: [
          { name: "Predictive Analytics", description: "Forecasting models for demand, churn, and business outcomes" },
          { name: "Customer Analytics", description: "Segmentation, lifetime value analysis, and behavior insights" },
          { name: "Marketing Analytics", description: "Campaign performance, attribution modeling, and ROI analysis" },
          { name: "Financial Analytics", description: "Financial modeling, risk analysis, and profitability insights" },
          { name: "Operational Analytics", description: "Process optimization, efficiency analysis, and performance improvement" }
        ]
      }
    ]
  },
  testimonials: [
    {
      quote: "The data analyst we hired through CareerBuddyPro created dashboards that completely changed how we make decisions. We can now spot trends and issues immediately.",
      name: "Robert Kim",
      title: "VP of Operations",
      company: "DataDriven Inc"
    },
    {
      quote: "The predictive analytics model reduced our customer churn by 30%. The insights were incredibly accurate and actionable.",
      name: "Lisa Chen",
      title: "Customer Success Director",
      company: "RetentionPro"
    },
    {
      quote: "Our marketing ROI improved by 60% after implementing the attribution analysis. We finally understand which channels drive real value.",
      name: "Mike Anderson",
      title: "Marketing Director",
      company: "AnalyticsCorp"
    }
  ],
  faqSection: [
    {
      question: "What data do I need for analytics projects?",
      answer: "Analytics projects can work with various data sources including CRM systems, website analytics, sales data, and customer surveys. Analysts help assess data quality and identify additional sources needed."
    },
    {
      question: "How long does it take to build analytics solutions?",
      answer: "Simple dashboards take 1-2 weeks, while complex predictive models can take 6-12 weeks. Timeline depends on data complexity, analysis scope, and integration requirements."
    },
    {
      question: "Do I need specific tools or software?",
      answer: "Analysts can recommend the best tools based on your needs and budget. Many solutions work with existing systems and popular platforms like Excel, Google Analytics, and CRM systems."
    },
    {
      question: "Will I be able to maintain the analytics solution myself?",
      answer: "Yes, most analysts provide training and documentation so your team can maintain and update dashboards and reports independently after project completion."
    }
  ],
  ctaSection: {
    title: "Unlock the Power of Your Data",
    description: "Stop making decisions based on gut feeling. Find analytics experts who turn your data into competitive advantages.",
    primaryButton: "Find Data Analysts",
    secondaryButton: "Get Analytics Quote"
  }
};

export default {
  contentCreationData,
  designServicesData,
  digitalMarketingData,
  businessConsultingData,
  dataAnalyticsData
};